import React, { useState, useEffect, useCallback } from 'react';
import './CreateOrderFooter.scss';
import { Button } from 'react-bootstrap';
import { MODAL_TYPES, ROLE_TYPES } from '@/constants/constants.js';

function CreateOrderFooter({
  formik_CreateOrder,
  footerStep,
  setFooterStep,
  setBarStep,
  formik_ClientInfo,
  setConfirmText,
  setModalType,
  setModalVisible,
  isConfirmButtonDisabled,
  isSpeechLengthOverflow,
  isFarewellLengthOverflow,
  role,
  selectedOrder,
}) {
  const [isButtonDisabled, setIsButtonDisabled] = useState(false);

  const showErrorModal = (errorType, customMessage = null) => {
    if (customMessage) {
      setModalType(MODAL_TYPES.验证失败);
      setConfirmText('好的');
      window.formValidationErrorMessage = customMessage;
    } else {
      setModalType(errorType);
      setConfirmText('确认');
    }
    setModalVisible(true);
    setIsButtonDisabled(false);
  };

  // 滚动到指定字段并高亮显示
  const scrollToField = (fieldName) => {
    setTimeout(() => {
      let element = null;

      // 特殊处理images字段 - 查找上传按钮
      if (fieldName === 'images') {
        element =
          document.querySelector('button[name="images"]') ||
          document.querySelector('.add-photo-button');
      } else {
        // 普通字段处理
        element = document.querySelector(`[name="${fieldName}"]`);
      }

      if (element) {
        // 滚动到字段位置
        element.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });

        // 对于按钮，不需要focus，直接添加高亮样式
        if (element.tagName.toLowerCase() === 'button') {
          element.classList.add('error-highlight');
        } else {
          // 对于输入框，先focus再添加样式
          element.focus();
          element.classList.add('error-highlight');
        }

        // 5秒后移除高亮样式
        setTimeout(() => {
          element.classList.remove('error-highlight');
        }, 3000);
      }
    }, 100);
  };

  // 验证表单并显示所有错误信息
  const validateFormAndShowErrors = async (formik, errorType) => {
    setIsButtonDisabled(true);

    try {
      // 触发表单验证
      const errors = await formik.validateForm();

      // 设置所有字段为已触摸状态
      await formik.setTouched(
        Object.keys(formik.initialValues).reduce((touched, key) => {
          touched[key] = true;
          return touched;
        }, {})
      );

      // 等待状态更新
      await new Promise((resolve) => setTimeout(resolve, 150));

      // 检查是否有验证错误
      if (Object.keys(errors).length > 0) {
        // 获取所有错误信息
        const errorMessages = Object.keys(errors).map((fieldName) => {
          const errorMessage = errors[fieldName];

          // 确保错误信息是字符串，如果是对象则提取其值
          const messageText =
            typeof errorMessage === 'object'
              ? errorMessage.description || JSON.stringify(errorMessage)
              : errorMessage;
          return `• ${messageText}`;
        });

        // 滚动到第一个错误字段
        const firstErrorField = Object.keys(errors)[0];
        scrollToField(firstErrorField);

        // 显示所有错误信息
        const allErrorsMessage = `请检查以下必填项：\n\n${errorMessages.join(
          '\n'
        )}`;
        showErrorModal(errorType, allErrorsMessage);

        setIsButtonDisabled(false);
        return false;
      }

      setIsButtonDisabled(false);
      return true;
    } catch (error) {
      console.error('表单验证失败:', error);
      showErrorModal(errorType, '表单验证失败，请检查输入内容');
      setIsButtonDisabled(false);
      return false;
    }
  };

  const handleFormSubmit = async (formik, errorType) => {
    try {
      setIsButtonDisabled(true);
      await formik.submitForm();
      await new Promise((resolve) => setTimeout(resolve, 100));

      // 检查表单错误
      if (
        Object.keys(formik.errors).length > 0 ||
        !formik.isValid ||
        formik.isSubmitting ||
        (formik.status && formik.status.success === false)
      ) {
        showErrorModal(errorType);
        formik.setSubmitting(false);
        return false;
      }
      setIsButtonDisabled(false);
      return true;
    } catch (error) {
      showErrorModal(errorType);
      formik.setSubmitting(false);
      setIsButtonDisabled(false);
      return false;
    }
  };

  const btns = [
    {
      footerStep: 1,
      nextBtn: '确认套餐信息，下一步',
    },
    {
      footerStep: 2,
      prevBtn: '上一步',
      nextBtn: '完成信息填写，确认下单',
    },
    {
      footerStep: 3,
      nextBtn: '材料上传完毕，下一步',
    },
    {
      footerStep: 4,
      nextBtn: '确认文案，提交审核',
    },
    {
      footerStep: 5,
      nextBtn: '重新提交',
    },
    {
      footerStep: 6,
      prevBtn: '审核不通过',
      nextBtn: '审核通过',
    },
  ];

  const currentBtn = btns.filter((s) => s.footerStep === footerStep)[0];

  const getIsDisabled = useCallback(() => {
    // 只在特定情况下禁用按钮，其他情况允许点击以触发验证
    if (footerStep === 1) {
      return !formik_CreateOrder.values.videoType;
    } else if (footerStep === 4) {
      return (
        role === ROLE_TYPES.客服 &&
        (isConfirmButtonDisabled ||
          isSpeechLengthOverflow ||
          isFarewellLengthOverflow)
      );
    }
    return false;
  }, [
    footerStep,
    formik_CreateOrder.values.videoType,
    role,
    isConfirmButtonDisabled,
    isSpeechLengthOverflow,
    isFarewellLengthOverflow,
  ]);

  useEffect(() => {
    setIsButtonDisabled(getIsDisabled());
  }, [getIsDisabled]);

  const handleNextBtnClick = async () => {
    if (footerStep === 1) {
      setBarStep(2);
      setFooterStep(2);
    } else if (footerStep === 2) {
      const isValid = await validateFormAndShowErrors(
        formik_CreateOrder,
        MODAL_TYPES.订单创建失败
      );
      if (!isValid) return;

      const success = await handleFormSubmit(
        formik_CreateOrder,
        MODAL_TYPES.订单创建失败
      );
      if (!success) return;
      setBarStep(3);
      setFooterStep(3);
    } else if (footerStep === 3) {
      const isValid = await validateFormAndShowErrors(
        formik_ClientInfo,
        MODAL_TYPES.订单提交失败
      );
      if (!isValid) return;

      const success = await handleFormSubmit(
        formik_ClientInfo,
        MODAL_TYPES.订单提交失败
      );
      if (!success) return;
    } else if (footerStep === 4) {
      setConfirmText('确认文案正确');
      setModalType(MODAL_TYPES.请再次确认文案);
      setModalVisible(true);
    } else if (footerStep === 6) {
      setModalType(MODAL_TYPES.确认审核通过);
      setConfirmText('审核通过，分配人员');
      setModalVisible(true);
    } else if (footerStep === 5) {
      const isValid = await validateFormAndShowErrors(
        formik_ClientInfo,
        MODAL_TYPES.订单提交失败
      );
      if (!isValid) return;

      const success = await handleFormSubmit(
        formik_ClientInfo,
        MODAL_TYPES.订单提交失败
      );
      if (!success) return;
    }
  };

  const handlePreBtnClick = async () => {
    if (footerStep === 2) {
      setBarStep(1);
      setFooterStep(1);
    } else if (footerStep === 2) {
      const success = await handleFormSubmit(
        formik_CreateOrder,
        MODAL_TYPES.订单创建失败
      );
      if (!success) return;

      setBarStep(3);
      setFooterStep(3);
    } else if (footerStep === 3) {
      const success = await handleFormSubmit(
        formik_ClientInfo,
        MODAL_TYPES.订单提交失败
      );
      if (!success) return;
    } else if (footerStep === 6) {
      //审核不通过
      setModalType(MODAL_TYPES.确认审核不通过);
      setConfirmText('确认审核不通过');
      setModalVisible(true);
    }
  };

  return (
    <div className="footer">
      {currentBtn && currentBtn.prevBtn && (
        <Button onClick={handlePreBtnClick}>{currentBtn.prevBtn}</Button>
      )}
      <Button onClick={handleNextBtnClick} disabled={isButtonDisabled}>
        {currentBtn.nextBtn}
      </Button>
    </div>
  );
}

export default CreateOrderFooter;
